import { useCallback, useEffect, useState } from 'react'
import { DateTime } from 'luxon'
import { capitalize, uniqBy } from 'lodash'

import { fetchInvoices, InvoiceResponse } from '@/reducers/subscription.slice'
import { useAppDispatch } from '@/utils/typeHelpers'
import { DATE_FORMATS_LUXON } from '@/utils/dateHelpers'
import { centsToDollars } from '@/utils/currencyHelpers'
import { CurrencyFormatLabel } from '@/components/CurrencyFormatLabel'
import { TypographyStyles } from '@/styles'
import { Badge, Button, Link, Text, type BadgeColor } from '@/ui'

const INVOICE_STATUS_BADGE_COLOR: Record<
  NonNullable<InvoiceResponse['status']>,
  BadgeColor
> = {
  paid: 'green',
  open: 'yellow',
  void: 'gray',
  uncollectible: 'red',
  draft: 'gray',
}

const PAID_AMOUNT_STATUSES = new Set<InvoiceResponse['status']>([
  'paid',
  'void',
])

const displayedAmountCents = (invoice: InvoiceResponse): number =>
  PAID_AMOUNT_STATUSES.has(invoice.status)
    ? invoice.amount_paid
    : invoice.amount_due

export const InvoiceList = () => {
  const dispatch = useAppDispatch()
  const [invoices, setInvoices] = useState<InvoiceResponse[]>([])
  const [hasMore, setHasMore] = useState(false)
  const lastInvoiceId = invoices[invoices.length - 1]?.id

  const fetchInvoicePage = useCallback(
    async (startingAfterInvoiceId?: string) => {
      const res = await dispatch(
        fetchInvoices({ starting_after_invoice_id: startingAfterInvoiceId })
      )

      if (res) {
        setInvoices((prevInvoices) =>
          uniqBy([...prevInvoices, ...res.data], 'id')
        )

        setHasMore(res.has_more)
      }
    },
    [dispatch]
  )

  useEffect(() => {
    fetchInvoicePage()
  }, [fetchInvoicePage])

  return (
    <div className="flex flex-col gap-0.5">
      <Text variant={TypographyStyles.EYEBROW}>Invoices</Text>

      {invoices.map((invoice) => (
        <div
          key={invoice.id}
          className="grid grid-cols-12 items-center gap-4 py-2"
        >
          <div className="col-span-5 sm:col-span-6 md:col-span-5">
            <Link href={invoice.hosted_invoice_url ?? '#'}>
              {DateTime.fromSeconds(invoice.created).toFormat(
                DATE_FORMATS_LUXON.DISPLAY_LONG
              )}
            </Link>
          </div>

          <div className="col-span-4 sm:col-span-3 md:col-span-4">
            <Text>
              <CurrencyFormatLabel
                value={centsToDollars(displayedAmountCents(invoice))}
              />
            </Text>
          </div>

          <div className="col-span-3">
            {invoice.status && (
              <Badge
                variant="secondary"
                color={INVOICE_STATUS_BADGE_COLOR[invoice.status] ?? 'gray'}
                size="sm"
              >
                {capitalize(invoice.status)}
              </Badge>
            )}
          </div>
        </div>
      ))}
      {invoices.length === 0 && (
        <Text variant={TypographyStyles.BODY_SM}>No invoices found</Text>
      )}
      {hasMore && (
        <Button onClick={() => fetchInvoicePage(lastInvoiceId)} variant="link">
          Show More
        </Button>
      )}
    </div>
  )
}
